/**
 * Case Manager API Service
 *
 * This service handles all case manager related API operations including:
 * - Listing case managers for a firm
 * - Assigning/changing/removing case managers from matters
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';
import { mattersApiClient } from './mattersService';
import { tasksApiClient } from './tasksService';

/** Simple user schema for case manager display */
export interface CaseManagerUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  display_name: string;
  /** Whether this CM is eligible for auto-assignment (from firm role config) */
  case_auto_assign?: boolean;
}

/** Request payload for assigning a case manager.
 * Note: Task reassignment is automatic - all eligible MP tasks are reassigned.
 */
export interface AssignCaseManagerRequest {
  case_manager_id: string;
  /** Whether the case manager should be automatically assigned MP tasks for this matter */
  auto_task_assignment?: boolean;
}

/** Request payload for changing a case manager.
 * Note: Task reassignment is automatic - all eligible tasks from old CM are reassigned.
 */
export interface ChangeCaseManagerRequest {
  new_case_manager_id: string;
  /** Whether the case manager should be automatically assigned MP tasks for this matter */
  auto_task_assignment?: boolean;
}

/** Request payload for removing a case manager */
export interface RemoveCaseManagerRequest {
  revert_tasks_to_mp: boolean;
}

/** Request payload for updating case manager configuration */
export interface UpdateCaseManagerConfigRequest {
  /** Whether the case manager should be automatically assigned MP tasks for this matter */
  auto_task_assignment: boolean;
}

/** Response from case manager assignment operations */
export interface CaseManagerAssignmentResult {
  tasks_reassigned: number;
}

/** Request payload for bulk assigning a case manager to multiple matters */
export interface BulkAssignCaseManagerRequest {
  case_manager_id: string;
  matter_ids: string[];
  auto_task_assignment: boolean;
}

/** Aggregate result of bulk assigning a case manager */
export interface BulkAssignCaseManagerResult {
  total_matters: number;
  successful: number;
  failed: number;
  skipped: number;
  total_tasks_reassigned: number;
}

/** Request payload for bulk removing a case manager from multiple matters */
export interface BulkRemoveCaseManagerRequest {
  matter_ids: string[];
  revert_tasks_to_mp: boolean;
}

/** Aggregate result of bulk removing a case manager */
export interface BulkRemoveCaseManagerResult {
  total_matters: number;
  successful: number;
  failed: number;
  skipped: number;
  total_tasks_reverted: number;
}

export const caseManagerApiClient = createApi({
  reducerPath: 'caseManagerApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['CaseManagers'],
  endpoints: (builder) => ({
    /**
     * Get list of users with CASE_MANAGER role for a firm
     */
    getFirmCaseManagers: builder.query<CaseManagerUser[], { firmId: string }>({
      query: ({ firmId }) => `api/firms/${firmId}/case-managers/`,
      providesTags: (result, error, { firmId }) => [
        { type: 'CaseManagers', id: firmId },
      ],
    }),

    /**
     * Assign a case manager to a matter
     * Automatically reassigns eligible tasks to the new case manager
     */
    assignCaseManager: builder.mutation<
      CaseManagerAssignmentResult,
      { matterId: string; data: AssignCaseManagerRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-manager/`,
        method: 'POST',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate matter cache to show updated case_manager
          dispatch(
            mattersApiClient.util.invalidateTags([
              { type: 'Matter', id: matterId },
              'Matters',
            ]),
          );
          // Invalidate tasks cache since tasks may have been reassigned
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),

    /**
     * Change the case manager on a matter
     * Automatically reassigns eligible tasks to the new case manager
     */
    changeCaseManager: builder.mutation<
      CaseManagerAssignmentResult,
      { matterId: string; data: ChangeCaseManagerRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-manager/`,
        method: 'PUT',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate matter cache to show updated case_manager
          dispatch(
            mattersApiClient.util.invalidateTags([
              { type: 'Matter', id: matterId },
              'Matters',
            ]),
          );
          // Invalidate tasks cache since tasks may have been reassigned
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),

    /**
     * Remove the case manager from a matter
     * Optionally reverts tasks back to the managing paralegal
     */
    removeCaseManager: builder.mutation<
      void,
      { matterId: string; data: RemoveCaseManagerRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-manager/`,
        method: 'DELETE',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate matter cache to show removed case_manager
          dispatch(
            mattersApiClient.util.invalidateTags([
              { type: 'Matter', id: matterId },
              'Matters',
            ]),
          );
          // Invalidate tasks cache since tasks may have been reassigned
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),

    /**
     * Update case manager configuration (auto_task_assignment setting)
     * When toggling auto_task_assignment to true, tasks may be reassigned to the CM
     */
    updateCaseManagerConfig: builder.mutation<
      CaseManagerAssignmentResult,
      { matterId: string; data: UpdateCaseManagerConfigRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-manager/`,
        method: 'PATCH',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate matter cache to show updated config
          dispatch(
            mattersApiClient.util.invalidateTags([
              { type: 'Matter', id: matterId },
              'Matters',
            ]),
          );
          // Invalidate tasks cache since tasks may have been reassigned
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),

    /**
     * Bulk assign a case manager to multiple matters
     * All matters must belong to the same firm
     */
    bulkAssignCaseManager: builder.mutation<
      BulkAssignCaseManagerResult,
      { data: BulkAssignCaseManagerRequest }
    >({
      query: ({ data }) => ({
        url: 'api/matters/bulk-assign-case-manager/',
        method: 'POST',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate all matters since multiple were updated
          dispatch(mattersApiClient.util.invalidateTags(['Matters']));
          // Invalidate tasks cache since tasks may have been reassigned
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),

    /**
     * Bulk remove a case manager from multiple matters
     * Matters without a case manager are skipped
     */
    bulkRemoveCaseManager: builder.mutation<
      BulkRemoveCaseManagerResult,
      { data: BulkRemoveCaseManagerRequest }
    >({
      query: ({ data }) => ({
        url: 'api/matters/bulk-remove-case-manager/',
        method: 'POST',
        body: data,
      }),
      // Cross-API cache invalidation
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate all matters since multiple were updated
          dispatch(mattersApiClient.util.invalidateTags(['Matters']));
          // Invalidate tasks cache since tasks may have been reverted
          dispatch(tasksApiClient.util.invalidateTags(['Tasks', 'TaskCounts']));
        } catch {
          // Mutation failed, no invalidation needed
        }
      },
    }),
  }),
});

export const {
  useGetFirmCaseManagersQuery,
  useAssignCaseManagerMutation,
  useChangeCaseManagerMutation,
  useRemoveCaseManagerMutation,
  useUpdateCaseManagerConfigMutation,
  useBulkAssignCaseManagerMutation,
  useBulkRemoveCaseManagerMutation,
} = caseManagerApiClient;
